import React, { useEffect, useMemo, useState } from 'react';
import { accountProfileApi, sistemaTsApi } from '../services/api';
import './SistemaTs.css';

const expenseTypes = [
  ['SR', 'Prestazione sanitaria'], ['TK', 'Ticket'], ['FC', 'Farmaco'],
  ['AS', 'Dispositivo medico'], ['CT', 'Cure termali'], ['PI', 'Protesica/integrativa'],
  ['SP', 'Prestazione psicologica'], ['SV', 'Prestazione veterinaria']
];

const today = () => new Date().toISOString().slice(0, 10);

export function SistemaTs() {
  const [health, setHealth] = useState('checking');
  const [operation, setOperation] = useState('insert');
  const [environment, setEnvironment] = useState('test');
  const [dryRun, setDryRun] = useState(true);
  const [confirmProduction, setConfirmProduction] = useState(false);
  const [profile, setProfile] = useState('');
  const [form, setForm] = useState({
    vatNumber: '', issueDate: today(), device: '1', documentNumber: '',
    paymentDate: today(), patientTaxCode: '', expenseType: 'SR', amount: '',
    trackedPayment: 'SI', documentType: 'F', opposition: '0',
    originalVatNumber: '', originalIssueDate: '', originalDevice: '1', originalDocumentNumber: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    sistemaTsApi.health().then(() => setHealth('online')).catch(() => setHealth('offline'));
    accountProfileApi.get().then(account => {
      setProfile(account.profile?.sistemaTsProfile || '');
      setForm(current => ({ ...current, vatNumber: current.vatNumber || account.profile?.vatNumber || '' }));
    }).catch(() => {});
  }, []);

  const canSubmit = useMemo(() => {
    if (!form.vatNumber || !form.issueDate || !form.documentNumber) return false;
    if (operation !== 'cancel' && (!form.paymentDate || !form.expenseType || !form.amount)) return false;
    if (operation === 'refund' && (!form.originalVatNumber || !form.originalIssueDate || !form.originalDocumentNumber)) return false;
    if (environment === 'production' && !confirmProduction) return false;
    return !submitting;
  }, [form, operation, environment, confirmProduction, submitting]);

  const update = (event) => setForm(current => ({ ...current, [event.target.name]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setResult(null);
    const document = {
      vat_number: form.vatNumber.trim(),
      issue_date: form.issueDate,
      device: Number(form.device),
      document_number: form.documentNumber.trim()
    };
    if (operation !== 'cancel') {
      Object.assign(document, {
        payment_date: form.paymentDate,
        patient_tax_code: form.patientTaxCode.trim().toUpperCase(),
        items: [{ type: form.expenseType, amount: Number(form.amount) }],
        tracked_payment: form.trackedPayment,
        document_type: form.documentType,
        opposition: form.opposition
      });
    }
    if (operation === 'refund') {
      document.original = {
        vat_number: form.originalVatNumber.trim(), issue_date: form.originalIssueDate,
        device: Number(form.originalDevice), document_number: form.originalDocumentNumber.trim()
      };
    }
    try {
      const response = await sistemaTsApi.submit(operation, document, {
        environment, dryRun, confirmProduction, profile: profile.trim()
      });
      setResult(response);
    } catch (err) {
      setError(err.message || 'Invio non riuscito');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ts-page">
      <div className="ts-heading">
        <div>
          <p className="ts-eyebrow">SIBILLA · INTEGRAZIONE FISCALE</p>
          <h1>Sistema <span>TS</span></h1>
          <p>Inserimento e gestione delle prestazioni sanitarie tramite i servizi ufficiali.</p>
        </div>
        <div className={`ts-status ${health}`}><span />{health === 'online' ? 'Servizio attivo' : health === 'offline' ? 'Non configurato' : 'Verifica…'}</div>
      </div>

      <form onSubmit={submit}>
        <section className="ts-card">
          <div className="ts-card-title"><span>Operazione</span><div><h2>Operazione</h2><p>Scegli il tipo di comunicazione e l’ambiente.</p></div></div>
          <div className="ts-grid ts-grid-4">
            <label>Operazione<select value={operation} onChange={e => setOperation(e.target.value)}><option value="insert">Inserimento</option><option value="vary">Variazione</option><option value="refund">Rimborso</option><option value="cancel">Cancellazione</option></select></label>
            <label>Ambiente<select value={environment} onChange={e => { setEnvironment(e.target.value); setConfirmProduction(false); }}><option value="test">Test ufficiale</option><option value="production">Produzione</option></select></label>
            <label>Profilo credenziali<input value={profile} onChange={e => setProfile(e.target.value)} placeholder="Predefinito" /></label>
            <label className="ts-check"><input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} /><span><strong>Solo simulazione</strong><small>Nessun invio esterno</small></span></label>
          </div>
        </section>

        <section className="ts-card">
          <div className="ts-card-title"><span>Documento</span><div><h2>Documento</h2><p>Dati identificativi della fattura o ricevuta.</p></div></div>
          <div className="ts-grid ts-grid-4">
            <label>Partita IVA<input name="vatNumber" value={form.vatNumber} onChange={update} inputMode="numeric" maxLength="11" placeholder="11 cifre" required /></label>
            <label>Data emissione<input type="date" name="issueDate" value={form.issueDate} onChange={update} required /></label>
            <label>Dispositivo<input type="number" min="1" max="999" name="device" value={form.device} onChange={update} required /></label>
            <label>Numero documento<input name="documentNumber" value={form.documentNumber} onChange={update} maxLength="20" required /></label>
          </div>
        </section>

        {operation !== 'cancel' && <section className="ts-card">
          <div className="ts-card-title"><span>Prestazione</span><div><h2>Prestazione</h2><p>Dati sanitari e fiscali trasmessi in forma cifrata.</p></div></div>
          <div className="ts-grid ts-grid-3">
            <label>Codice fiscale assistito<input name="patientTaxCode" value={form.patientTaxCode} onChange={update} maxLength="16" placeholder="Opzionale in caso di opposizione" /></label>
            <label>Data pagamento<input type="date" name="paymentDate" value={form.paymentDate} onChange={update} required /></label>
            <label>Tipo spesa<select name="expenseType" value={form.expenseType} onChange={update}>{expenseTypes.map(([value, label]) => <option key={value} value={value}>{value} · {label}</option>)}</select></label>
            <label>Importo (€)<input type="number" min="0.01" step="0.01" name="amount" value={form.amount} onChange={update} required /></label>
            <label>Pagamento tracciato<select name="trackedPayment" value={form.trackedPayment} onChange={update}><option value="SI">Sì</option><option value="NO">No</option></select></label>
            <label>Opposizione<select name="opposition" value={form.opposition} onChange={update}><option value="0">No</option><option value="1">Sì</option></select></label>
          </div>
        </section>}

        {operation === 'refund' && <section className="ts-card">
          <div className="ts-card-title"><span>Rettifica</span><div><h2>Documento originale</h2><p>Riferimento alla prestazione oggetto del rimborso.</p></div></div>
          <div className="ts-grid ts-grid-4">
            <label>Partita IVA<input name="originalVatNumber" value={form.originalVatNumber} onChange={update} maxLength="11" required /></label>
            <label>Data emissione<input type="date" name="originalIssueDate" value={form.originalIssueDate} onChange={update} required /></label>
            <label>Dispositivo<input type="number" min="1" max="999" name="originalDevice" value={form.originalDevice} onChange={update} required /></label>
            <label>Numero documento<input name="originalDocumentNumber" value={form.originalDocumentNumber} onChange={update} maxLength="20" required /></label>
          </div>
        </section>}

        {environment === 'production' && <label className="ts-production-confirm"><input type="checkbox" checked={confirmProduction} onChange={e => setConfirmProduction(e.target.checked)} /><span><strong>Confermo l’invio in produzione</strong><small>L’operazione sarà trasmessa realmente al Sistema TS.</small></span></label>}

        {error && <div className="alert alert-danger">{error}</div>}
        {result && <div className="ts-result"><strong>{result.dry_run ? 'Simulazione validata' : 'Operazione elaborata'}</strong><span>{result.intent?.protocol ? `Protocollo ${result.intent.protocol}` : `Stato: ${result.intent?.status || result.result?.outcome || 'completato'}`}</span></div>}

        <div className="ts-actions">
          <div><strong>{dryRun ? 'Modalità sicura' : environment === 'test' ? 'Invio ambiente test' : 'Invio reale'}</strong><small>{dryRun ? 'Il documento viene validato senza uscire da Sibilla.' : 'Controlla i dati prima di procedere.'}</small></div>
          <button className={environment === 'production' && !dryRun ? 'danger' : ''} disabled={!canSubmit}>{submitting ? 'Elaborazione…' : dryRun ? 'Valida documento' : 'Invia al Sistema TS'}</button>
        </div>
      </form>
    </div>
  );
}

export default SistemaTs;
